import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import {z} from 'zod';
import {
  type GetRecruitmentIdByDetailIdResponse,
  InternalPreviewCodeQueryParam,
  PreviewCodeQueryParam,
  RecruitmentDetailResource,
  type RecruitmentDetailResponse,
} from '../../../core/entities/recruitment/schema';
import {
  getRecruitmentDetail,
  getRecruitmentIdByDetailId,
} from '../../../core/repositories/recruitmentRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {ArchivesPage} from '../../../features/recruitment/components/ArchivesPage';
import {PreviewModeTag} from '../../../features/recruitment/components/Container/Presenter/PreviewModeTag';
import {RecruitmentDetailLayout} from '../../../features/recruitment/components/RecruitmentDetailLayout';
import {filterInternalPreviewCodeParams} from '../../../features/recruitment/usecases/filterInternalPreviewCodeParams';
import {handlePreviewError} from '../../../features/recruitment/usecases/handlePreviewError';
import {
  type RecruitmentDetailResourceWithParsedContent,
  parseResourceContents,
} from '../../../features/recruitment/usecases/parseResourceContent';
import {withHandler} from '../../../server/token/withHandler';
import {ScriptForGA} from '../../../shared/components/ScriptForGA';
import type {NextPageWithLayout} from '../../_app';

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  recruitmentItemId: number;
  recruitmentDetail: RecruitmentDetailResponse;
  recruitmentDetailId: number;
  parsedResourceContents: RecruitmentDetailResourceWithParsedContent[];
  decodedResources?: RecruitmentDetailResource[];
  isLogin?: boolean;
};

const QueryParameter = PreviewCodeQueryParam.merge(
  InternalPreviewCodeQueryParam,
);
type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);

    const recruitmentDetailId = Number(context.params?.id);
    const {
      preview_code,
      internal_preview_code,
      sex,
      bun_ri,
      university,
      current_univ_type,
      resources,
    } = context.validatedQuery;

    const getRecruitmentItemIdResponse = await getRecruitmentIdByDetailId({
      cookies,
      presenter: 'admin',
      detailId: recruitmentDetailId,
    });
    if (!getRecruitmentItemIdResponse.ok) {
      return {
        notFound: true,
      };
    }

    const internalPreviewCodeParams = filterInternalPreviewCodeParams({
      internal_preview_code,
      sex,
      bun_ri,
      university,
      current_univ_type,
    });
    const recruitmentIdData =
      getRecruitmentItemIdResponse.value as GetRecruitmentIdByDetailIdResponse;
    const {recruitmentID: recruitmentItemId} = recruitmentIdData;
    const getRecruitmentDetailResponse = await getRecruitmentDetail({
      cookies,
      recruitmentID: recruitmentItemId,
      preview_code,
      ...internalPreviewCodeParams,
    });

    const previewError = handlePreviewError(getRecruitmentDetailResponse);
    if (previewError) {
      return {props: previewError as any};
    }

    if (!getRecruitmentDetailResponse.ok) {
      return {
        notFound: true,
      };
    }

    // "resources"がある場合は、resourcesをBase64デコードしてパースする(admin側の本文のみプレビュー用)
    if (resources) {
      // プレビューコードなしのresourcesは受け付けない
      if (!internal_preview_code) {
        return {
          notFound: true,
        };
      }
      // 不正なresourcesは受け付けない
      let decodedResources: RecruitmentDetailResource[];
      try {
        const decodedString = Buffer.from(resources, 'base64').toString('utf8');
        decodedResources = z
          .array(RecruitmentDetailResource)
          .parse(JSON.parse(decodedString));
      } catch {
        return {
          notFound: true,
        };
      }
      const parsedResourceContents =
        await parseResourceContents(decodedResources);
      return {
        props: {
          recruitmentItemId,
          recruitmentDetail: getRecruitmentDetailResponse.value,
          recruitmentDetailId,
          parsedResourceContents,
          decodedResources,
          isLogin: isValid,
        },
      };
    }

    const parsedResourceContents = await parseResourceContents(
      getRecruitmentDetailResponse.value.resources,
    );
    const props: Props = {
      recruitmentItemId,
      recruitmentDetail: getRecruitmentDetailResponse.value,
      recruitmentDetailId,
      parsedResourceContents,
      isLogin: isValid,
    };

    if (!isValid) {
      return {props};
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  recruitmentItemId,
  recruitmentDetail,
  recruitmentDetailId,
  parsedResourceContents,
  decodedResources,
  isLogin,
}: Props) => {
  return (
    <>
      {recruitmentDetail.isPreview && (
        <div className="pc:p-0 px-8">
          <PreviewModeTag />
        </div>
      )}
      <ArchivesPage
        recruitmentItemId={recruitmentItemId}
        recruitmentDetail={recruitmentDetail}
        recruitmentDetailId={recruitmentDetailId}
        parsedResourceContents={parsedResourceContents}
        decodedResources={decodedResources}
        isLogin={isLogin ?? false}
      />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default Page;

Page.getLayout = (page) => (
  <RecruitmentDetailLayout>{page}</RecruitmentDetailLayout>
);
